import { GoToMealsRequest } from "@/clients/models/GoToMealsRequest";
import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import FilledButton from "@/components/button/filled-button";
import { QueryKeys } from "@/constants/query-keys";
import { useGetGotoMeal } from "@/hooks/requests/go-to/meal/use-get-goto-meal";
import { useSelectGotoMeals } from "@/hooks/requests/go-to/meal/use-select-goto-meals";
import { useToast } from "@/hooks/ui/use-toast";
import { IonButton, IonIcon } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { close, heart, refresh } from "ionicons/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import "swiper/css";
import "swiper/css/pagination";
import { SwiperClass } from "swiper/react";
import { useActionHistory } from "../../hooks/use-action-history";
import { MealSwiper } from "./meal-swiper";

interface GoToMealSwiperProps {
  mealTime: MealTime;
  meals: Meal[];
  onCompleted?: () => void;
}

export default function GoToMealSwiper({
  mealTime,
  meals,
  onCompleted,
}: GoToMealSwiperProps) {
  const swapperRef = useRef<SwiperClass | null>(null);
  const isProgrammatic = useRef(false);

  const [activeIndex, setActiveIndex] = useState(0);
  const {
    logLikeAction,
    logDislikeAction,
    logNavigateToAction,
    getLastAction,
    popLastAction,
  } = useActionHistory();
  const [selectedMealIds, setSelectedMealIds] = useState<Set<string>>(
    new Set(),
  );
  const { selectedMeals } = useGetGotoMeal(mealTime);
  const queryClient = useQueryClient();
  const { selectGotoMeal, isSelecting } = useSelectGotoMeals(mealTime);
  const { showSuccess, showInfo, showError, showWarning } = useToast();

  useEffect(() => {
    return () => {
      swapperRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (selectedMeals?.mealTime === mealTime) {
      if (selectedMeals?.meals) {
        // Prevent the out of date meals after the candidate meals are changed
        const mealSet = new Set<string>();
        selectedMeals.meals.forEach((selectedMeal) => {
          meals.forEach((meal) => {
            if (meal.id === selectedMeal.id) {
              mealSet.add(meal.id ?? "");
            }
          });
        });
        setSelectedMealIds(mealSet);
        requestAnimationFrame(() => {
          const firstSelectedMealIndex = meals.findIndex((meal) =>
            mealSet.has(meal.id ?? ""),
          );
          console.log("firstSelectedMealIndex", firstSelectedMealIndex);
          swapperRef.current?.slideTo(firstSelectedMealIndex);
        });
      }
    }
  }, [selectedMeals?.mealTime, selectedMeals?.meals]);

  const likeMeal = useCallback(() => {
    const currentMeal = meals[activeIndex];
    if (selectedMealIds.size === 2) {
      showWarning("You can only select 2 meals");
      return;
    }
    if (!currentMeal || !currentMeal.id) {
      return;
    }
    setSelectedMealIds((prev) => {
      const newSet = new Set(prev);
      newSet.add(currentMeal.id ?? "");
      if (newSet.size === 2) {
        showInfo(`You have selected 2 meals, you can save and continue`);
      } else if (newSet.size === 1) {
        showInfo(
          `You have selected 1 meal, you can select one more or save and continue`,
        );
      }
      return newSet;
    });
  }, [activeIndex, meals, selectedMealIds.size, showSuccess]);

  const dislikeMeal = useCallback(() => {
    const currentMeal = meals[activeIndex];
    if (!currentMeal || !currentMeal.id) {
      return;
    }
    setSelectedMealIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(currentMeal.id ?? "");
      return newSet;
    });
  }, [activeIndex, meals]);

  const onSwiperSlideChange = useCallback(
    (swiper: SwiperClass) => {
      setActiveIndex(() => {
        const newIndex = swiper.activeIndex;
        if (newIndex > meals.length - 1) {
          return activeIndex;
        }
        const nextMeal = meals[newIndex];
        if (!isProgrammatic.current) {
          logNavigateToAction(nextMeal?.id ?? "");
        }

        return newIndex;
      });
    },
    [activeIndex],
  );

  const onSwiperTransitionEnd = useCallback(() => {
    isProgrammatic.current = false;
  }, []);

  const onRollbackButtonClick = useCallback(() => {
    const lastAction = popLastAction();
    if (!lastAction) {
      return;
    }
    const secondLastAction = getLastAction();
    const secondLastIndex =
      meals.findIndex((meal) => meal.id === secondLastAction?.mealId) ?? 0;
    isProgrammatic.current = true;
    switch (lastAction?.action) {
      case "navigate":
        swapperRef.current?.slideTo(secondLastIndex);
        break;
      case "like":
        dislikeMeal();
        break;
      case "dislike":
        likeMeal();
        break;
    }
  }, [activeIndex, getLastAction]);

  const onNoButtonClick = useCallback(() => {
    if (isProgrammatic.current) {
      return;
    }
    isProgrammatic.current = true;
    const nextIndex = activeIndex + 1;
    logNavigateToAction(meals[nextIndex]?.id ?? "");
    swapperRef.current?.slideTo(nextIndex);
  }, [activeIndex]);

  const onLikeButtonClick = useCallback(() => {
    const currentMeal = meals[activeIndex];
    if (!currentMeal || !currentMeal.id) {
      return;
    }
    if (selectedMealIds.has(currentMeal.id)) {
      logDislikeAction(currentMeal?.id ?? "");
      dislikeMeal();
      return;
    } else {
      logLikeAction(currentMeal?.id ?? "");
      likeMeal();
    }
  }, [
    activeIndex,
    meals,
    selectedMealIds,
    logLikeAction,
    dislikeMeal,
    likeMeal,
  ]);

  const onSaveButtonClick = useCallback(() => {
    if (selectedMealIds.size === 0) {
      showInfo("Please select at least 1 meal");
      return;
    }
    const requestBody: GoToMealsRequest = {
      meals: Array.from(selectedMealIds),
    };
    selectGotoMeal(requestBody, {
      onSuccess: () => {
        showSuccess("Selected meals successfully");
        queryClient.removeQueries({
          queryKey: [QueryKeys.GO_TO_SELECT_MEAL, mealTime],
        });
        onCompleted?.();
        swapperRef.current?.slideTo(0);
      },
      onError: () => {
        showError("Failed to select meal");
      },
    });
  }, [selectedMealIds]);

  if (meals.length === 0) {
    return (
      <div className="w-full h-[20rem] flex justify-center items-center p-8">
        <div className="text-center py-8">
          <p className="text-gray-600">
            No meal candidates available for {mealTime.toLowerCase()}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full flex-1 flex flex-col gap-4">
        <div className="w-full h-[20rem] flex justify-center items-center">
          <MealSwiper
            swapperRef={swapperRef}
            meals={meals}
            onSwiperSlideChange={onSwiperSlideChange}
            onSwiperTransitionEnd={onSwiperTransitionEnd}
          />
        </div>
        <span className="w-full h-16 text-center text-lg font-semibold text-gray-900 capitalize">
          {meals[activeIndex]?.name}
        </span>

        <div className="flex justify-center items-center gap-6 mt-6">
          <IonButton
            fill="solid"
            color="light"
            shape="round"
            className="w-12 h-12"
            onClick={onRollbackButtonClick}
            disabled={isSelecting}
          >
            <IonIcon icon={refresh} />
          </IonButton>

          <IonButton
            fill="solid"
            color="light"
            shape="round"
            className="w-12 h-12"
            onClick={onNoButtonClick}
            disabled={isSelecting}
          >
            <IonIcon icon={close} />
          </IonButton>

          <IonButton
            fill="solid"
            color="dark"
            shape="round"
            className="w-12 h-12"
            disabled={
              isSelecting ||
              (selectedMealIds.size === 2 &&
                !selectedMealIds.has(meals[activeIndex]?.id ?? ""))
            }
            onClick={onLikeButtonClick}
          >
            <IonIcon
              icon={heart}
              color={
                selectedMealIds.has(meals[activeIndex]?.id ?? "")
                  ? "danger"
                  : "light"
              }
            />
          </IonButton>
        </div>
      </div>
      <div className="px-4 w-full">
        <FilledButton
          text="Save & Continue"
          color="dark"
          expand="full"
          shape="round"
          onClick={onSaveButtonClick}
          disabled={isSelecting}
        />
      </div>
    </>
  );
}
